import AsyncStorage from "@react-native-async-storage/async-storage";
import Config from "../constants/Config";
import Colors from "../constants/Colors";

export const APP_THEME_STORAGE_KEY = "appThemeDesigner.v1";
export const MAX_UPLOADED_FONTS = 12;
export const MAX_WEB_FONT_BYTES = 1024 * 1024 * 2;

const basePrimary = String(Config.companyColor || Colors?.light?.tint || "#2563eb");
const defaultBodyFont = String(Config?.theme?.defaultFontFamily || "System");
const defaultTitleFont = String(Config?.theme?.titleFontFamily || defaultBodyFont);
const defaultLogoFont = String(Config?.theme?.logoFontFamily || defaultBodyFont);

export const BUILTIN_FONT_OPTIONS = [
  { label: "System Default", value: defaultBodyFont },
  { label: "Helvetica", value: "Helvetica Neue, Helvetica, Arial, sans-serif" },
  { label: "Avenir", value: "Avenir Next, Avenir, Helvetica, Arial, sans-serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Trebuchet", value: "Trebuchet MS, Verdana, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times", value: "Times New Roman, Times, serif" },
  { label: "Courier", value: "Courier New, Courier, monospace" },
];

export const DEFAULT_APP_THEME = {
  primary: basePrimary,
  secondary: "#16a34a",
  accent: "#f59e0b",
  backgroundLight: String(Colors?.light?.background || "#ffffff"),
  backgroundDark: String(Colors?.dark?.background || "#0b1220"),
  surfaceLight: "#f8fafc",
  surfaceDark: "#111827",
  textLight: String(Colors?.light?.text || "#111827"),
  textDark: String(Colors?.dark?.text || "#f9fafb"),
  logoFontFamily: defaultLogoFont,
  titleFontFamily: defaultTitleFont,
  bodyFontFamily: defaultBodyFont,
  uploadedFonts: [],
};

export const APP_THEME_PRESETS = [
  {
    id: "mexicali",
    name: "Mexicali",
    theme: {
      primary: "#d62828",
      secondary: "#2e7d32",
      accent: "#1565c0",
      backgroundLight: "#fffdf7",
      backgroundDark: "#0b1220",
      surfaceLight: "#ffffff",
      surfaceDark: "#10263d",
      textLight: "#14213d",
      textDark: "#f8fafc",
    },
  },
  {
    id: "tex-mex",
    name: "Tex-Mex",
    theme: {
      primary: "#e85d04",
      secondary: "#ffb703",
      accent: "#219ebc",
      backgroundLight: "#fff5eb",
      backgroundDark: "#2b1b12",
      surfaceLight: "#ffffff",
      surfaceDark: "#3d2519",
      textLight: "#352208",
      textDark: "#fff7ed",
    },
  },
  {
    id: "pacific-rim",
    name: "Pacific Rim",
    theme: {
      primary: "#0ea5e9",
      secondary: "#06b6d4",
      accent: "#a8e0ef",
      backgroundLight: "#e8fbff",
      backgroundDark: "#10243a",
      surfaceLight: "#f7feff",
      surfaceDark: "#15304d",
      textLight: "#0f2e3a",
      textDark: "#e6f8ff",
    },
  },
  {
    id: "california-dreamin",
    name: "California Dreamin'",
    theme: {
      primary: "#ff3d81",
      secondary: "#ff8a00",
      accent: "#f7c38f",
      backgroundLight: "#fff4de",
      backgroundDark: "#2a180f",
      surfaceLight: "#fff9ef",
      surfaceDark: "#3b2417",
      textLight: "#3a1f14",
      textDark: "#fff3e0",
    },
  },
];

let currentTheme = null;
const listeners = new Set();

function normalizeHexColor(value, fallback) {
  const text = String(value || "").trim().toLowerCase();
  const shortHex = /^#[0-9a-f]{3}$/;
  const fullHex = /^#[0-9a-f]{6}$/;
  if (fullHex.test(text)) {
    return text;
  }
  if (shortHex.test(text)) {
    return `#${text[1]}${text[1]}${text[2]}${text[2]}${text[3]}${text[3]}`;
  }
  return fallback;
}

function normalizeFontFamily(value, fallback) {
  const text = String(value || "").trim();
  return text || fallback;
}

function sanitizeFontIdentifier(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || `font-${Date.now().toString(36)}`;
}

function normalizeUploadedFonts(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }

      const id = sanitizeFontIdentifier(entry.id || entry.family || entry.name);
      const name = String(entry.name || "").trim() || id;
      const family = String(entry.family || "").trim() || `ThemeUpload-${id}`;
      const dataUrl = String(entry.dataUrl || "").trim();
      if (!dataUrl.startsWith("data:font/") && !dataUrl.startsWith("data:application/")) {
        return null;
      }

      return {
        id,
        name,
        family,
        dataUrl,
        mimeType: String(entry.mimeType || "").trim() || "font/ttf",
        size: Number(entry.size) > 0 ? Number(entry.size) : undefined,
      };
    })
    .filter(Boolean)
    .slice(0, MAX_UPLOADED_FONTS);
}

function cloneTheme(theme) {
  if (!theme || typeof theme !== "object") {
    return { ...DEFAULT_APP_THEME, uploadedFonts: [] };
  }
  return {
    ...theme,
    uploadedFonts: Array.isArray(theme.uploadedFonts)
      ? theme.uploadedFonts.map((font) => ({ ...font }))
      : [],
  };
}

function normalizeTheme(rawTheme) {
  const raw = rawTheme && typeof rawTheme === "object" ? rawTheme : {};
  const uploadedFonts = normalizeUploadedFonts(raw.uploadedFonts);
  return {
    primary: normalizeHexColor(raw.primary, DEFAULT_APP_THEME.primary),
    secondary: normalizeHexColor(raw.secondary, DEFAULT_APP_THEME.secondary),
    accent: normalizeHexColor(raw.accent, DEFAULT_APP_THEME.accent),
    backgroundLight: normalizeHexColor(raw.backgroundLight, DEFAULT_APP_THEME.backgroundLight),
    backgroundDark: normalizeHexColor(raw.backgroundDark, DEFAULT_APP_THEME.backgroundDark),
    surfaceLight: normalizeHexColor(raw.surfaceLight, DEFAULT_APP_THEME.surfaceLight),
    surfaceDark: normalizeHexColor(raw.surfaceDark, DEFAULT_APP_THEME.surfaceDark),
    textLight: normalizeHexColor(raw.textLight, DEFAULT_APP_THEME.textLight),
    textDark: normalizeHexColor(raw.textDark, DEFAULT_APP_THEME.textDark),
    logoFontFamily: normalizeFontFamily(raw.logoFontFamily, DEFAULT_APP_THEME.logoFontFamily),
    titleFontFamily: normalizeFontFamily(raw.titleFontFamily, DEFAULT_APP_THEME.titleFontFamily),
    bodyFontFamily: normalizeFontFamily(raw.bodyFontFamily, DEFAULT_APP_THEME.bodyFontFamily),
    uploadedFonts,
  };
}

function applyUploadedFontFaces(theme) {
  if (typeof document === "undefined") {
    return;
  }

  const uploadedFonts = normalizeUploadedFonts(theme?.uploadedFonts);
  uploadedFonts.forEach((font) => {
    const styleId = `theme-font-face-${sanitizeFontIdentifier(font.id)}`;
    let styleTag = document.getElementById(styleId);
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      styleTag.type = "text/css";
      document.head.appendChild(styleTag);
    }

    const family = String(font.family || "").replace(/'/g, "\\'");
    const source = String(font.dataUrl || "").replace(/'/g, "\\'");
    styleTag.textContent = `@font-face { font-family: '${family}'; src: url('${source}'); font-display: swap; }`;
  });
}

export function registerThemeUploadedFonts(themeValue) {
  applyUploadedFontFaces(themeValue || currentTheme || DEFAULT_APP_THEME);
}

function applyThemeToGlobals(theme) {
  const next = normalizeTheme(theme);

  Config.companyColor = next.primary;
  Config.theme.defaultFontFamily = next.bodyFontFamily;
  Config.theme.titleFontFamily = next.titleFontFamily;
  Config.theme.logoFontFamily = next.logoFontFamily;

  if (Colors?.light) {
    Colors.light.tint = next.primary;
    Colors.light.tabIconSelected = next.primary;
    Colors.light.background = next.backgroundLight;
    Colors.light.text = next.textLight;
    Colors.light.underlay = next.surfaceLight;
  }

  if (Colors?.dark) {
    Colors.dark.tint = next.primary;
    Colors.dark.tabIconSelected = next.primary;
    Colors.dark.background = next.backgroundDark;
    Colors.dark.text = next.textDark;
    Colors.dark.underlay = next.surfaceDark;
  }

  applyUploadedFontFaces(next);
}

function notifyThemeSubscribers() {
  listeners.forEach((listener) => {
    try {
      listener(currentTheme);
    } catch (_error) {
      // Ignore subscriber failures.
    }
  });
}

export function getDefaultAppTheme() {
  return cloneTheme(DEFAULT_APP_THEME);
}

export function getCurrentAppTheme() {
  return currentTheme ? cloneTheme(currentTheme) : null;
}

export function createUploadedWebFontEntry(fileName, dataUrl, mimeType, size) {
  const safeName = String(fileName || "Uploaded Font").trim() || "Uploaded Font";
  const baseName = safeName.replace(/\.[^.]+$/, "");
  const slug = sanitizeFontIdentifier(baseName);
  const stamp = Date.now().toString(36);
  const id = `${slug}-${stamp}`;
  return {
    id,
    name: safeName,
    family: `ThemeUpload-${id}`,
    dataUrl: String(dataUrl || "").trim(),
    mimeType: String(mimeType || "").trim() || "font/ttf",
    size: Number(size) > 0 ? Number(size) : undefined,
  };
}

export function getThemeFontOptions(themeValue) {
  const theme = normalizeTheme(themeValue || currentTheme || DEFAULT_APP_THEME);
  const seen = new Set();
  const options = [];

  BUILTIN_FONT_OPTIONS.forEach((option) => {
    if (!option?.value || seen.has(option.value)) {
      return;
    }
    seen.add(option.value);
    options.push({
      label: option.label,
      value: option.value,
      uploaded: false,
    });
  });

  theme.uploadedFonts.forEach((font) => {
    if (!font?.family || seen.has(font.family)) {
      return;
    }
    seen.add(font.family);
    options.push({
      label: `Uploaded: ${font.name}`,
      value: font.family,
      uploaded: true,
      id: font.id,
    });
  });

  return options;
}

export function removeUploadedFont(themeValue, fontId) {
  const theme = normalizeTheme(themeValue);
  const target = sanitizeFontIdentifier(fontId);
  const removedFont = theme.uploadedFonts.find((font) => sanitizeFontIdentifier(font.id) === target);
  const uploadedFonts = theme.uploadedFonts.filter((font) => sanitizeFontIdentifier(font.id) !== target);

  const next = {
    ...theme,
    uploadedFonts,
  };

  if (removedFont && removedFont.family === next.logoFontFamily) {
    next.logoFontFamily = DEFAULT_APP_THEME.logoFontFamily;
  }
  if (removedFont && removedFont.family === next.titleFontFamily) {
    next.titleFontFamily = DEFAULT_APP_THEME.titleFontFamily;
  }
  if (removedFont && removedFont.family === next.bodyFontFamily) {
    next.bodyFontFamily = DEFAULT_APP_THEME.bodyFontFamily;
  }

  return next;
}

export async function loadAppTheme() {
  try {
    const stored = await AsyncStorage.getItem(APP_THEME_STORAGE_KEY);
    if (!stored) {
      currentTheme = null;
      applyThemeToGlobals(DEFAULT_APP_THEME);
      notifyThemeSubscribers();
      return null;
    }

    const parsed = JSON.parse(stored);
    const normalized = normalizeTheme(parsed);
    currentTheme = normalized;
    applyThemeToGlobals(normalized);
    notifyThemeSubscribers();
    return cloneTheme(normalized);
  } catch (error) {
    console.warn("[ThemeDesigner] Failed to load stored theme:", error);
    currentTheme = null;
    applyThemeToGlobals(DEFAULT_APP_THEME);
    notifyThemeSubscribers();
    return null;
  }
}

export async function saveAppTheme(nextTheme) {
  const baseline = currentTheme || DEFAULT_APP_THEME;
  const normalized = normalizeTheme({ ...baseline, ...(nextTheme || {}) });
  await AsyncStorage.setItem(APP_THEME_STORAGE_KEY, JSON.stringify(normalized));
  currentTheme = normalized;
  applyThemeToGlobals(normalized);
  notifyThemeSubscribers();
  return cloneTheme(normalized);
}

export async function resetAppTheme() {
  await AsyncStorage.removeItem(APP_THEME_STORAGE_KEY);
  currentTheme = null;
  applyThemeToGlobals(DEFAULT_APP_THEME);
  notifyThemeSubscribers();
}

export function subscribeAppTheme(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }
  listeners.add(listener);
  return () => listeners.delete(listener);
}
